<template>
    <b-modal
        :id="`modal-form-${modalId}`"
        ref="modal"
        :title="$t('edit')"
        size="lg"
        ok-variant="primary"
        :ok-title="$t('save')"
        static
        centered
        ok-only
        :busy="submitting"
        @ok.prevent="handleSubmit()"
        @show="resetData()"
        @hidden="resetData()"
    >

        <FormValidator form-group :validator="$v.form.name" v-slot="slotProps" :label="$t('productName')">
            <b-form-input v-model="form.name" :state="slotProps.state" :disabled="submitting" />
        </FormValidator>
        <FormValidator form-group :validator="$v.form.description" v-slot="slotProps" :label="$t('description')">
            <b-form-textarea v-model="form.description" rows="5" :state="slotProps.state" :disabled="submitting" />
        </FormValidator>
        <FormValidator v-if="!itemId" form-group :validator="$v.form.ean" v-slot="slotProps" :label="$t('ean')">
            <b-form-input v-model="form.ean" :state="slotProps.state" :disabled="submitting" />
        </FormValidator>
        <FormValidator v-if="!itemId" form-group :validator="$v.form.sku" v-slot="slotProps" :label="$t('sku')">
            <b-form-input v-model="form.sku" :state="slotProps.state" :disabled="submitting" />
        </FormValidator>
        <FormValidator form-group :validator="$v.form.price" v-slot="slotProps" :label="$t('catalogPrice')">
            <b-form-input v-model="form.price" :state="slotProps.state" :disabled="submitting" />
        </FormValidator>
        <FormValidator :validator="$v.form.categories[0]" form-group v-slot="slotProps" :label="$t('category')">
            <FormSelectTree
                v-if="spaceId"
                ref="FormTreeSelect"
                v-model="form.categories[0]"
                :disabled="submitting"
                :state="slotProps.state"
                :load-options="loadOptions"
                :normalizer="normalizer"
            />
        </FormValidator>

        <b-form-group id="form-group-images" :label="$t('photos')">
            <div class="d-flex flex-column gap-3">
                <div v-for="(item, index) in form.images" :key="index" class="d-flex gap-3">
                    <FormValidator :id="`form-group-images-${index}-url`" class="d-flex flex-column flex-grow-2 flex-shrink-0 flex-basis-0" :validator="$v.form[`images-${index}-url`]" v-slot="slotProps">
                        <b-form-input :id="`form-images-${index}-url`" v-model="form.images[index]" :placeholder="$t('urlAddress')" :disabled="submitting" :state="slotProps.state" />
                    </FormValidator>
                    <ButtonIcon
                        :disabled="submitting"
                        variant="danger"
                        icon="trash-2"
                        :title="$t('remove')"
                        class="mb-auto"
                        @click="removeImage(index)"
                    />
                </div>
                <ButtonIcon :disabled="submitting" class="wide mr-auto" variant="outline-success" @click="addImage()">
                    {{ $t('addPhoto') }}
                </ButtonIcon>
            </div>
        </b-form-group>

        <template #modal-footer="{ ok }">
            <ButtonIcon :loading="submitting" class="wide" @click="ok">
                {{ $t('save') }}
            </ButtonIcon>
        </template>

    </b-modal>
</template>



<script>
    import { required } from 'vuelidate/lib/validators';
    import FormValidator from '@/components/common/FormValidator.vue';
    import FormSelectTree from '@/components/common/FormSelectTree.vue';
    import modalFormMixin from '@/mixins/modalFormMixin';
    import cloneDeep from 'lodash/cloneDeep';
    import { url } from '@/utils/validators';

    const FORM_DEFAULT_DATA = Object.freeze({
        name: null,
        description: null,
        ean: null,
        sku: null,
        price: null,
        categories: [],
        images: [],
    });

    export default {
        name: 'ModalAddProduct',
        mixins: [modalFormMixin],
        components: {
            FormValidator,
            FormSelectTree,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestProductsTree: this.$req('space/ProductsTree'),
                form: cloneDeep(FORM_DEFAULT_DATA),
                modalId: 'producer-products-product',
            };
        },
        computed: {
            createAction() {
                return this.$req('space/producerProducts').post;
            },
            updateAction() {
                return this.parentComponent.requestProduct.patch;
            },
            spaceId() {
                return this.item?.space.id;
            },
            formValidations() {
                const form = {
                    name: { required },
                    description: {},
                    price: {},
                    categories: {
                        0: { required },
                    },
                    ...this.form.images.reduce(
                        (acc, item, index) => ({
                            ...acc,
                            ...{
                                [`images-${index}-url`]: {
                                    url: () => (item ? url(item) : true),
                                },
                            },
                        }),
                        {}
                    ),
                };
                if (!this.itemId) {
                    form.ean = { required };
                    form.sku = { required };
                }
                return {
                    form,
                };
            },
            apiParams() {
                const urlParams = this.itemId ? { id: this.itemId } : { spaceId: this.spaceId };
                return {
                    urlParams,
                    requestParams: this.form,
                };
            },
        },
        methods: {
            fetchProductsTree(categoryId) {
                return this.requestProductsTree.get({ urlParams: { spaceId: this.spaceId }, requestParams: { category: categoryId } });
            },
            loadOptions({ parentNode }) {
                return this.fetchProductsTree(parentNode?.entityId);
            },
            normalizer(node) {
                return {
                    id: node.entityId,
                    label: node.text,
                    children: node.children === true ? null : node.children,
                    isDisabled: node.type === 'product',
                };
            },
            addImage() {
                this.form.images.push('');
            },
            removeImage(index) {
                this.form.images.splice(index, 1);
            },
            afterInitFormValues() {
                if (this.itemId) {
                    delete this.form.ean;
                    delete this.form.sku;
                    this.form.categories = [this.item.categories[0][this.item.categories[0].length - 1].id];
                }
            },
        },
    };
</script>
